import type { FacetType } from '@seek/chalice-types';
import {
  Button,
  Inline,
  Stack,
  Text,
  Box,
  IconAI,
  TextLinkButton,
  useResponsiveValue,
  useToast,
  Heading,
} from 'braid-design-system';
import { useState, useMemo } from 'react';

import { getRefineSearchQuery } from 'src/components/Search/utils/utils';
import { useHotjarEvent } from 'src/hooks/useHotjarEvent';
import { useDispatch, useSelector } from 'src/store/react';
import { updateKeywordsField } from 'src/store/search';
import { useRunSearch } from 'src/store/search/useRunSearch';
import { selectSearchQuery } from 'src/store/selectors';
import { filterFacetNudgePillsParams } from 'src/utils/filterFacetNudgePillsParams';
import { getFacetLabelText } from 'src/utils/getFacetLabelText';

import { NudgePill } from './NudgePill';
import { MAX_VISIBLE_PILLS_COLLAPSED, MAX_DISPLAY_PILLS } from './constants';

interface NudgeIntentProps {
  facetTypesData: FacetType[];
  title: string;
  description: string;
  displayToastMessage: string;
  isMultiSelect: boolean;
  facetKey: string;
}

export const NudgeIntent = ({
  facetTypesData,
  title,
  description,
  displayToastMessage,
  isMultiSelect,
  facetKey,
}: NudgeIntentProps) => {
  const dispatch = useDispatch();
  const isMobile =
    useResponsiveValue()({
      mobile: true,
      tablet: false,
    }) || false;

  const showToast = useToast();
  const query = getRefineSearchQuery(useSelector(selectSearchQuery));
  const runSearch = useRunSearch();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState(false);

  // filter out salaryMin with label '0' first as we dont want to show it as a nudge pill
  const filterSalaryMin = facetTypesData.filter(({ label }) => label !== '0');
  const maximumNudgePillsToShow = filterSalaryMin.slice(0, MAX_DISPLAY_PILLS);
  const showMoreOrLessPillsButton =
    maximumNudgePillsToShow.length > MAX_VISIBLE_PILLS_COLLAPSED;

  const nudgePillsToDisplay = expanded
    ? maximumNudgePillsToShow
    : maximumNudgePillsToShow.slice(0, MAX_VISIBLE_PILLS_COLLAPSED);

  const numberOfHiddenPills =
    maximumNudgePillsToShow.length - MAX_VISIBLE_PILLS_COLLAPSED;

  const nudgePillsWithActiveState = useMemo(
    () =>
      nudgePillsToDisplay
        // Filter out pills with empty labels (string or object)
        .filter((pill) => getFacetLabelText(pill.label) !== '')
        .map((pill, index) => ({
          ...pill,
          isActive: selectedIds.has(pill.id),
          index,
        })),
    [nudgePillsToDisplay, selectedIds],
  );

  const handleNudgePillClick = (pillId: string) => {
    setSelectedIds((previousSelection) => {
      const wasSelected = previousSelection.has(pillId);

      if (isMultiSelect) {
        const newSet = new Set(previousSelection);
        if (wasSelected) {
          newSet.delete(pillId);
        } else {
          newSet.add(pillId);
        }
        return newSet;
      }
      // Single-select
      return wasSelected ? new Set() : new Set([pillId]);
    });
  };

  const nudgePills = nudgePillsWithActiveState?.map(({ id, label, count }) => (
    <NudgePill
      id={id}
      key={id}
      label={getFacetLabelText(label, facetKey)}
      count={count}
      isActive={selectedIds.has(id)}
      isMobile={isMobile}
      onClick={handleNudgePillClick}
    />
  ));

  const selectedJobsCount = useMemo(
    () =>
      Array.from(selectedIds).reduce(
        (sum, id) =>
          sum +
          (maximumNudgePillsToShow.find((pill) => pill.id === id)?.count || 0),
        0,
      ),
    [selectedIds, maximumNudgePillsToShow],
  );

  const handleSelectedJobsCountButtonClick = () => {
    if (facetKey === 'distinctTitle') {
      // single-select for distinctTitle
      // convert the set to an array to get value
      const selectedId = Array.from(selectedIds)[0];
      const pillSelected = maximumNudgePillsToShow.find(
        (p) => p.id === selectedId,
      );
      const label = getFacetLabelText(pillSelected?.label);
      dispatch(updateKeywordsField(label));
      dispatch(
        runSearch({
          pathname: '/jobs',
        }),
      );
    } else {
      // Other facetKeys - workType, workArrangement, salaryMin
      dispatch(
        runSearch({
          pathname: '/jobs',
          overrideQuery: {
            ...query,
            ...filterFacetNudgePillsParams(facetKey, selectedIds),
          },
        }),
      );
    }
    showToast({
      message: displayToastMessage,
      tone: 'positive',
    });
  };

  useHotjarEvent('srp_in_flow_nudging_experiment');

  return (
    <Box
      padding="medium"
      boxShadow="borderPromoteLight"
      borderRadius="large"
      data-automation={`nudge-intent-${facetKey}`}
    >
      <Stack space="medium">
        <Stack space="small">
          <Inline space="none" alignY="center">
            <Heading level="4" weight="regular">
              <IconAI tone="promote" />
            </Heading>
            <Text
              weight="strong"
              tone="promote"
              size={isMobile ? 'small' : 'standard'}
            >
              {title}
            </Text>
          </Inline>
          <Text size={isMobile ? 'small' : 'standard'}>{description}</Text>
        </Stack>
        <Inline space={{ mobile: 'xxsmall', tablet: 'xsmall' }} alignY="center">
          {nudgePills}
          {showMoreOrLessPillsButton && (
            <Box
              data-automation="showMoreOrLessPillsButton"
              component="button"
              onClick={() => setExpanded(!expanded)}
            >
              <Text size="small">
                <TextLinkButton weight="weak">
                  {expanded ? 'Show less' : `+${numberOfHiddenPills} more`}
                </TextLinkButton>
              </Text>
            </Box>
          )}
        </Inline>
        {selectedIds.size > 0 && (
          <Button
            data-automation="selectedJobsCountButton"
            onClick={handleSelectedJobsCountButtonClick}
            variant="solid"
            tone="brandAccent"
          >
            SEEK {selectedJobsCount.toLocaleString()} jobs
          </Button>
        )}
      </Stack>
    </Box>
  );
};
